import React, { useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import { Col, Row, Title, Button, MessageBanner } from '@ix/ix-ui'
import BulkAutoUpdateForm from './BulkAutoUpdateForm'
import { BulkUpdateContextType, BulkUpdateSPUDRecordType } from './BulkUpdateContext.type'
import { appendNewFieldsToOriginalForProcessing, BulkUpdateFlowStateType } from './BulkAutoUpdateService'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCheckCircle, faTimes, faTimesCircle, faTrash } from '@fortawesome/free-solid-svg-icons'
import { IconProp } from '@fortawesome/fontawesome-svg-core'
import { SPUDServiceRecordDetails } from '../../../@types/Service.type'

const SPUDServiceForm = styled.div`
  width: 70%;
`

const ServiceLeftPanel = styled.div`
  display: table-cell;
  padding: 0;
  margin-left: 15px;
  background-color: #515257;
`

const ServiceList = styled.ul`
  padding: 0;
`

const FormContainer = styled.div`
  width: 100%;
  margin-left: 0;
  padding: 1em;
  background-color: #fff;
`

const AnimatedBannerWrapper = styled.div`
  margin: 10px 0 -8px 10px;
  @keyframes shake {
    0%, 100% { transform: translateX(0); }
    20%  { transform: translateX(-5px); }
    40%  { transform: translateX(5px); }
    60%  { transform: translateX(-3px); }
    80%  { transform: translateX(3px); }
  }
  animation: shake 0.4s ease;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`

const ServiceItemIcons = styled.span`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
`

const ServiceListItem = styled.li<{active: boolean}>`
  display: block;
  padding: 10px;
  border: 0;
  font-family: inherit;
  font-size: 14px;
  text-align: left;
  color: #252223;
  background-color: ${props => props.active ? '#fff' : '#d1d2d4'};
  margin-bottom: 10px;
  margin-right: ${props => props.active ? '0' : '1em'};
  margin-left: 1em;
  position: relative;
  :hover {
    cursor: pointer;
  }
`

type ServiceFormProps = {
  recordId: number,
  setBulkAutoUpdateFlowState: (flowState: BulkUpdateFlowStateType) => void
  setNavigationGuard: (fn: () => Promise<boolean>) => void
} & BulkUpdateContextType

function ServiceForm (
  {
    setBulkAutoUpdateFlowState,
    saveServiceData,
    services,
    setActiveServiceIndexContext,
    activeServiceIndexContext,
    site,
    setDialogOptions,
    setNavigationGuard,
  }: ServiceFormProps) {
  const [activeServiceIndex, setActiveServiceIndex] = useState(0)
  const [showValidationBanner, setShowValidationBanner] = useState(false)
  const [bannerKey, setBannerKey] = useState(0)
  const [serviceErrors, setServiceErrors] = useState<{[x: number]: boolean}>({})

  const showErrorValidationBanner = () => {
    setShowValidationBanner(true)
    setBannerKey(k => k + 1)
  }

  // Scroll to the top whenever the active service index changes
  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    })
  }, [activeServiceIndex])

  const triggerFormValidationRef = useRef<() => Promise<boolean>>(
    async () => {
      return true
    },
  )

  const validateCurrentForm = async (): Promise<boolean> => {
    const valid = await triggerFormValidationRef.current()
    if (!valid) {
      showErrorValidationBanner()
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }
    return valid
  }

  useEffect(() => {
    setNavigationGuard(validateCurrentForm)
    return () => setNavigationGuard(async () => true)
  }, [])

  const addNewService = async () => {
    const valid = await validateCurrentForm()
    if (!valid) {
      return
    }

    const nextIndex = services.length || 0
    setShowValidationBanner(false)

    saveServiceData([
      ...services,
      {
        recordType: 'service',
        currentData: {},
        originalData: {},
        comment: '',
        confirmed: false,
        deleted: false,
        noChanges: false,
      },
    ])

    setActiveServiceIndex(nextIndex)
    setActiveServiceIndexContext(nextIndex)
  }

  const handleServiceSwitch = async (index: number) => {
    const valid = await validateCurrentForm()
    if (!valid) {
      return
    }

    setShowValidationBanner(false)
    setActiveServiceIndex(index)
    setActiveServiceIndexContext(index)
  }

  useEffect(() => {
    activeServiceIndexContext !== null && setActiveServiceIndex(activeServiceIndexContext)
  }, [activeServiceIndexContext])

  const deleteNewService = (index: number) => {
    setActiveServiceIndex(0)
    setActiveServiceIndexContext(0)
    const newServiceList = services.filter((_, i) => i !== index)
    saveServiceData(newServiceList)

    const reindex = (prev: {[x: number]: boolean}) => {
      const updated: {[x: number]: boolean} = {}
      Object.keys(prev).forEach(key => {
        const k = Number(key)
        if (k < index) updated[k] = prev[k]
        else if (k > index) updated[k - 1] = prev[k]
      })
      return updated
    }
    setServiceErrors(reindex)
  }

  const confirmDeleteNewService = (index: number) => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
    setDialogOptions({
      onConfirm: () => deleteNewService(index),
      title: 'Delete service',
      description: 'Are you sure you want to delete this new service? It will not be created.',
      show: true,
      type: 'confirm',
    })
  }

  return (
    <SPUDServiceForm>
      <Row style={{ padding: '1em' }}>
        <Col>
          <Title level={1}>
            Update service
          </Title>
        </Col>
      </Row>
      <Row style={{ padding: '1em' }}>
        <Col>
          <hr style={{ width: '100%' }} />
        </Col>
      </Row>
      <Row>
        <Col style={{ padding: 0 }}>
          <ServiceLeftPanel>
            <ServiceList>
              {services.map((service, index) => (
                <ServiceListItem
                  key={index}
                  active={activeServiceIndex === index}
                  onClick={() => {
                    if (services?.[activeServiceIndex]) {
                      if (activeServiceIndex !== index) {
                        services[activeServiceIndex] = {
                          recordType: 'service',
                          currentData: services[activeServiceIndex]?.currentData,
                          originalData: appendNewFieldsToOriginalForProcessing(
                            services[activeServiceIndex],
                          ) as Record<string, never> | BulkUpdateSPUDRecordType<SPUDServiceRecordDetails>,
                          comment: services[activeServiceIndex].comment,
                          confirmed: services[activeServiceIndex].confirmed,
                          deleted: services[activeServiceIndex].deleted,
                          noChanges: services[activeServiceIndex].noChanges,
                        }
                        saveServiceData(services)
                        handleServiceSwitch(index)
                      }
                    }
                  }}
                >
                  <Row>
                    <Col direction='row' align='center' justify='space-between'>
                      {service.currentData.update?.data?.name || 'New Service'}
                      <ServiceItemIcons>
                        {serviceErrors[index] && service.currentData.update?.data?.name && (
                          <FontAwesomeIcon
                            icon={faTimesCircle as IconProp}
                            color='red'
                          />
                        )}
                        {!serviceErrors[index] && service.deleted && (
                          <span title='This service is marked for deletion'>
                            <FontAwesomeIcon
                              icon={faTrash as IconProp}
                              color='gray'
                            />
                          </span>
                        )}
                        {!serviceErrors[index] &&
                          !service.deleted &&
                          service.currentData.update?.data?.name &&
                          service.currentData.update?.data?.description &&
                          (service.confirmed || service.noChanges) && (
                          <FontAwesomeIcon
                            icon={faCheckCircle as IconProp}
                            color='green'
                          />
                        )}
                        {!service.currentData?.id && (
                          <Button
                            onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                              event.preventDefault()
                              event.stopPropagation()
                              confirmDeleteNewService(index)
                            }}
                            margin='0'
                            padding='0 5px'
                            title='Delete new service'
                          >
                            <FontAwesomeIcon
                              icon={faTimes as IconProp}
                              color='#F03931'
                            />
                          </Button>
                        )}
                      </ServiceItemIcons>
                    </Col>
                  </Row>
                </ServiceListItem>
              ))}
              <Button onClick={() => addNewService()}>
                Add new service
              </Button>
            </ServiceList>
          </ServiceLeftPanel>
        </Col>
        <Col flex={4}>
          {showValidationBanner && (
            <AnimatedBannerWrapper key={bannerKey}>
              <MessageBanner
                margin='-5px 12px 20px'
                message='Please fix validation errors before continuing.'
              />
            </AnimatedBannerWrapper>
          )}
          <FormContainer>
            {activeServiceIndex >= 0 && services.length > 0 && services?.[activeServiceIndex] && (
              <>
                <BulkAutoUpdateForm
                  recordId={activeServiceIndex.toString()}
                  recordType='service'
                  formData={services[activeServiceIndex].currentData.update?.data}
                  siteData={services[activeServiceIndex].currentData.site}
                  originalData={
                    services[activeServiceIndex].originalData.update?.data || services[activeServiceIndex].originalData
                  }
                  contextUpdater={(
                    data: SPUDServiceRecordDetails,
                    comment: string,
                    confirm: boolean,
                    noChanges,
                    deleted,
                  ) => {
                    let currentData = services[activeServiceIndex].currentData
                    let originalData = services[activeServiceIndex].originalData
                    if (!currentData?.id && !currentData.update?.data?.site) {
                      data = {
                        ...data,
                        datasets: site.currentData.update?.data.datasets,
                        site: site.currentData.id,
                        is_active: true,
                      }
                      originalData = {
                        update: {
                          data: data,
                        },
                      } as BulkUpdateSPUDRecordType<SPUDServiceRecordDetails>
                    }
                    currentData = {
                      ...currentData,
                      update: {
                        ...services[activeServiceIndex].currentData.update,
                        data: {
                          ...services[activeServiceIndex].currentData?.update?.data || {},
                          ...data,
                        },
                      },
                    } as BulkUpdateSPUDRecordType<SPUDServiceRecordDetails>
                    services[activeServiceIndex] = {
                      recordType: 'service',
                      currentData: currentData,
                      originalData: originalData,
                      comment: comment,
                      confirmed: confirm,
                      deleted: deleted,
                      noChanges: noChanges,
                    }
                    saveServiceData(services)
                  }}
                  confirmed={services[activeServiceIndex].confirmed}
                  noChanges={services[activeServiceIndex].noChanges}
                  willBeDeleted={services[activeServiceIndex].deleted}
                  comment={services[activeServiceIndex].comment}
                  activeServiceIndexContext={activeServiceIndexContext}
                  setHasErrors={(hasErrors) => {
                    if (!hasErrors) setShowValidationBanner(false)
                    setServiceErrors(prev => ({ ...prev, [activeServiceIndex]: hasErrors }))
                  }}
                  setTrigger={(fn) => {
                    triggerFormValidationRef.current = fn
                  }}
                  onValidationFailed={() => showErrorValidationBanner()}
                  onNext={() => {
                    services[activeServiceIndex] = {
                      recordType: 'service',
                      currentData: services[activeServiceIndex].currentData,
                      originalData: services[activeServiceIndex].originalData,
                      comment: services[activeServiceIndex].comment,
                      confirmed: services[activeServiceIndex].confirmed,
                      deleted: services[activeServiceIndex].deleted,
                      noChanges: services[activeServiceIndex].noChanges,
                    }
                    saveServiceData(services)
                    const nextIndex = activeServiceIndex + 1
                    setActiveServiceIndex(nextIndex)
                    if (nextIndex === services.length) {
                      setActiveServiceIndexContext(null)
                      window.scrollTo({
                        top: 0,
                      })
                      setBulkAutoUpdateFlowState('review')
                    } else {
                      services[nextIndex] = {
                        recordType: 'service',
                        currentData: services[nextIndex].currentData,
                        originalData: services[nextIndex].originalData,
                        comment: services[nextIndex].comment,
                        confirmed: services[nextIndex].confirmed,
                        deleted: services[nextIndex].deleted,
                        noChanges: services[nextIndex].noChanges,
                      }
                      saveServiceData(services)
                      window.scrollTo({
                        top: 0,
                      })
                    }
                  }}
                  onPrevious={() => {
                    services[activeServiceIndex] = {
                      recordType: 'service',
                      currentData: services[activeServiceIndex].currentData,
                      originalData: services[activeServiceIndex].originalData,
                      comment: services[activeServiceIndex].comment,
                      confirmed: services[activeServiceIndex].confirmed,
                      deleted: services[activeServiceIndex].deleted,
                      noChanges: services[activeServiceIndex].noChanges,
                    }
                    setActiveServiceIndexContext(null)
                    saveServiceData(services)
                    setBulkAutoUpdateFlowState('site')
                  }}
                  isNewService={!services[activeServiceIndex].currentData?.id}
                  setDialogOptions={setDialogOptions}
                />
              </>
            )}
            {services.length === 0 && (
              <Row>
                <Col direction='row' justify='space-between'>
                  <Title level={4}>
                    Nothing to update, click next to proceed to the review and submit.
                  </Title>
                  <Button active onClick={() => setBulkAutoUpdateFlowState('review')}>
                    Next
                  </Button>
                </Col>
              </Row>
            )}
          </FormContainer>
        </Col>
      </Row>
    </SPUDServiceForm>
  )
}

export default ServiceForm
